import React, { useState } from 'react';
import { X, Shield, Palette, Key, AlertTriangle, Users, CheckCircle2, Loader2, Wrench, Search, XCircle } from 'lucide-react';
import { TwoFactorAuthSettings } from './TwoFactorAuthSettings';
import { TemporaryTwoFADisable } from './TemporaryTwoFADisable';
import { ThemeToggle } from './ThemeToggle';
import { PasswordChange } from './PasswordChange';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const SuperadminSettingsModal = ({ isOpen, onClose, user }) => {
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Diagnose-Tool State
  const [diagnoseEmail, setDiagnoseEmail] = useState('');
  const [diagnoseLoading, setDiagnoseLoading] = useState(false);
  const [diagnoseResult, setDiagnoseResult] = useState(null);

  const handlePasswordReset = async () => {
    setMigrationLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/superadmin/maintenance/reset-employee-passwords`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMigrationResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Zurücksetzen');
    } finally {
      setMigrationLoading(false);
      setShowConfirm(false);
    }
  };

  const handleDiagnose = async () => {
    if (!diagnoseEmail.trim()) return;
    setDiagnoseLoading(true);
    setDiagnoseResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/superadmin/maintenance/diagnose-login`,
        { email: diagnoseEmail.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDiagnoseResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler bei der Diagnose');
    } finally {
      setDiagnoseLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-[60]" 
        onClick={onClose}
      />
      
      {/* Modal – auf Mobile: Vollbild von unten, auf Desktop: zentriert */}
      <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center sm:p-4 pointer-events-none">
        <div className="bg-card shadow-2xl border border-border w-full sm:max-w-2xl
                        rounded-t-2xl sm:rounded-lg
                        max-h-[92dvh] sm:max-h-[90vh]
                        overflow-hidden flex flex-col pointer-events-auto">

          {/* Header – kompakt auf Mobile */}
          <div className="flex items-center justify-between px-4 py-3 sm:p-6 border-b border-border bg-gradient-to-r from-blue-600 to-blue-700 text-white flex-shrink-0">
            <div>
              <h2 className="text-lg sm:text-2xl font-bold leading-tight">Einstellungen</h2>
              <p className="text-xs sm:text-sm text-white/80 mt-0.5">Superadmin-Sicherheit & Präferenzen</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0 ml-2"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>

          {/* Content – scrollbar, iOS-kompatibel */}
          <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:p-6 space-y-5 sm:space-y-6"
               style={{ WebkitOverflowScrolling: 'touch' }}>

            {/* 2FA Settings */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex-shrink-0" />
                <h3 className="text-base sm:text-lg font-semibold">Zwei-Faktor-Authentifizierung</h3>
              </div>
              <TemporaryTwoFADisable user={user} />
              <TwoFactorAuthSettings user={user} />
            </div>

            <div className="border-t border-border" />

            {/* Design/Theme */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Palette className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 flex-shrink-0" />
                <h3 className="text-base sm:text-lg font-semibold">Design</h3>
              </div>
              <div className="bg-muted/50 p-3 rounded-lg">
                <ThemeToggle />
              </div>
            </div>

            <div className="border-t border-border" />

            {/* Password Change */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Key className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600 flex-shrink-0" />
                <h3 className="text-base sm:text-lg font-semibold">Passwort ändern</h3>
              </div>
              <PasswordChange user={user} />
            </div>

            <div className="border-t border-border" />

            {/* Login-Diagnose Tool */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 flex-shrink-0" />
                <h3 className="text-base sm:text-lg font-semibold">Login-Diagnose</h3>
              </div>
              <p className="text-xs text-muted-foreground">
                Prüfen Sie warum sich ein Benutzer nicht einloggen kann.
              </p>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={diagnoseEmail}
                  onChange={e => setDiagnoseEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleDiagnose()}
                  placeholder="mitarbeiter@example.de"
                  className="flex-1 h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                  data-testid="diagnose-email-input"
                />
                <button
                  onClick={handleDiagnose}
                  disabled={diagnoseLoading || !diagnoseEmail.trim()}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="diagnose-btn"
                >
                  {diagnoseLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  Prüfen
                </button>
              </div>

              {diagnoseResult && (
                <div className={`rounded-lg border p-3 space-y-2 text-xs ${diagnoseResult.found ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50' : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20'}`}
                     data-testid="diagnose-result">
                  {!diagnoseResult.found ? (
                    <div className="flex items-start gap-2">
                      <XCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-red-800 dark:text-red-200">{diagnoseResult.problem}</p>
                        {diagnoseResult.similar_accounts?.length > 0 && (
                          <p className="mt-1 text-red-700 dark:text-red-300">
                            Ähnlich: {diagnoseResult.similar_accounts.map(a => a.email).join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span className="font-semibold">{diagnoseResult.user_info.name} ({diagnoseResult.user_info.role})</span>
                      </div>
                      <div className="grid grid-cols-2 gap-1 bg-white dark:bg-gray-800 rounded p-2">
                        <span className={diagnoseResult.user_info.is_active ? 'text-green-700 dark:text-green-400' : 'text-red-700 font-bold'}>
                          {diagnoseResult.user_info.is_active ? '✅' : '❌'} is_active
                        </span>
                        <span className={diagnoseResult.user_info.email_verified ? 'text-green-700 dark:text-green-400' : 'text-red-700 font-bold'}>
                          {diagnoseResult.user_info.email_verified ? '✅' : '❌'} email_verified
                        </span>
                        <span className={diagnoseResult.password_test.pin_1234_works ? 'text-green-700 dark:text-green-400' : 'text-red-700 font-bold'}>
                          {diagnoseResult.password_test.pin_1234_works ? '✅' : '❌'} PIN 1234 gültig
                        </span>
                        <span className="text-gray-500">
                          {diagnoseResult.user_info.must_change_password ? '🔒' : '✅'} Passwort-Pflicht
                        </span>
                      </div>
                      <div className="space-y-1">
                        {diagnoseResult.blockers.map((b, i) => (
                          <p key={i} className={b.startsWith('✅') ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-300 font-semibold'}>{b}</p>
                        ))}
                      </div>
                      {diagnoseResult.recommended_fix && (
                        <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded p-2">
                          <p className="text-blue-800 dark:text-blue-200"><strong>Fix:</strong> {diagnoseResult.recommended_fix}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-border" />

            {/* Wartung & Migration */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Wrench className="h-4 w-4 sm:h-5 sm:w-5 text-orange-500 flex-shrink-0" />
                <h3 className="text-base sm:text-lg font-semibold">Wartung & Daten-Migration</h3>
              </div>

              <div className="border-2 border-orange-200 dark:border-orange-800 rounded-lg p-3 space-y-3 bg-orange-50 dark:bg-orange-950/20">
                <div>
                  <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                    Mitarbeiter-Logins reparieren (Reset auf 1234)
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Setzt alle Mitarbeiter-Passwörter auf <strong>1234</strong> zurück und erzwingt eine Passwortänderung beim nächsten Login.
                  </p>
                </div>

                {migrationResult && (
                  <div className="flex items-start gap-2 bg-green-50 dark:bg-green-950/30 border border-green-300 dark:border-green-700 rounded-lg p-3">
                    <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-green-800 dark:text-green-200 font-medium" data-testid="migration-result">
                      {migrationResult.message}
                    </p>
                  </div>
                )}

                {!showConfirm ? (
                  <button
                    className="w-full px-4 py-2.5 border-2 border-orange-400 text-orange-700 dark:text-orange-300 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                    onClick={() => { setShowConfirm(true); setMigrationResult(null); }}
                    data-testid="reset-passwords-btn"
                  >
                    <Users className="h-4 w-4" />
                    Mitarbeiter-Logins reparieren
                  </button>
                ) : (
                  <div className="border border-red-300 dark:border-red-700 rounded-lg p-3 space-y-3 bg-red-50 dark:bg-red-950/20">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-red-800 dark:text-red-200 font-semibold">
                        Alle Mitarbeiter-Passwörter auf "1234" zurücksetzen?
                      </p>
                    </div>
                    <p className="text-xs text-red-700 dark:text-red-300 pl-6">
                      Betrifft ALLE Mitarbeiter & Stationsleitungen. Jeder muss beim nächsten Login ein neues Passwort setzen.
                    </p>
                    <div className="flex gap-2 pl-6">
                      <button
                        className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium flex items-center gap-1.5 disabled:opacity-60"
                        onClick={handlePasswordReset}
                        disabled={migrationLoading}
                        data-testid="confirm-reset-btn"
                      >
                        {migrationLoading ? (
                          <><Loader2 className="h-3 w-3 animate-spin" /> Läuft...</>
                        ) : 'Ja, zurücksetzen'}
                      </button>
                      <button
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-xs font-medium hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-60"
                        onClick={() => setShowConfirm(false)}
                        disabled={migrationLoading}
                        data-testid="cancel-reset-btn"
                      >
                        Abbrechen
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Extra Padding unten für iOS Safe Area */}
            <div className="h-2 sm:h-0" />
          </div>

          {/* Footer */}
          <div className="px-4 py-3 sm:p-4 border-t border-border bg-muted/50 flex-shrink-0">
            <button
              onClick={onClose}
              className="w-full px-4 py-3 sm:py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium text-sm"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
